'use client';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
// Font definitions are removed, will be handled by root app/layout.tsx
// import { cn } from '@/lib/utils';
// Language prop is removed, LanguageProvider will pick up from context or URL segment via hooks
import { appConfig } from '@/config/appConfig';
import { analytics as firebaseAnalyticsPromise } from '@/lib/firebaseAnalytics';
import Navbar from '@/components/client/layout/Navbar';
import type { Analytics } from 'firebase/analytics';

// Dynamically import Toaster
const ToasterComponent = dynamic(
    () => import('@/components/ui/sonner').then((mod) => mod.Toaster),
    {
        ssr: false,
        loading: () => null,
    }
);

interface PageShellProps {
    children: React.ReactNode;
}

export function PageShell({ children }: PageShellProps) {
    useEffect(() => {
        const trackPageView = async () => {
            if (appConfig.featureFlags?.enableAnalytics) {
                try {
                    const analyticsInstance: Analytics | null =
                        await firebaseAnalyticsPromise;
                    if (analyticsInstance) {
                        const { logEvent } = await import('firebase/analytics');
                        logEvent(analyticsInstance, 'page_view', {
                            page_path: window.location.pathname,
                            page_title: document.title,
                        });
                        console.log(
                            '[PageShell] Page view logged to Firebase Analytics via explicit logEvent.'
                        );
                    } else {
                        console.log(
                            '[PageShell] Firebase Analytics instance is null, explicit page_view not logged. This might be due to missing measurementId or an initialization error.'
                        );
                    }
                } catch (error) {
                    console.error(
                        '[PageShell] Error initializing or using Firebase Analytics for explicit page_view logging:',
                        error
                    );
                }
            }
        };

        trackPageView();
        // This effect runs once on mount. For SPA navigation tracking,
        // this would need to be re-triggered based on route changes (e.g., using usePathname).
        // For now, we are fixing the immediate error.
        // Note: gtag('config') in GoogleAnalyticsScripts.tsx likely handles the initial page_view.
        // This explicit call might be redundant or for specific cases.
    }, []); // Empty dependency array, runs once on mount

    return (
        <div
            className="relative flex min-h-dvh flex-col bg-background"
            style={{
                backgroundImage:
                    'var(--havana-bg-pattern), linear-gradient(oklch(var(--background)), oklch(var(--background)))',
                backgroundSize: 'var(--havana-bg-pattern-size), auto',
                backgroundAttachment: 'fixed',
            }}
        >
            <Navbar />
            <main className="flex-1">{children}</main>
            <ToasterComponent
                position="top-right"
                richColors
                closeButton
                duration={5000}
            />
        </div>
    );
}
